"use client";

import { FormEvent, RefObject } from "react";
import { SearchEvent, formatSuggestionDate } from "./landing-nav-types";

type SearchPanelProps = {
    searchValue: string;
    onSearchChange: (value: string) => void;
    onSearchSubmit: (event: FormEvent) => void;
    onSearchFocus: () => void;
    hasQuery: boolean;
    isSearchFocused: boolean;
    isSearchOpen: boolean;
    isSearching: boolean;
    searchResults: SearchEvent[];
    onSuggestionSelect: (event: SearchEvent) => void;
    formRef: RefObject<HTMLFormElement | null>;
    datePickerSlot: React.ReactNode;
};

const buildSuggestionMeta = (event: SearchEvent) => {
    const parts = [formatSuggestionDate(event.date)];
    const location = event.locationName || event.city;
    if (location) {
        parts.push(location);
    }
    if (event.hostName) {
        parts.push(event.hostName);
    }
    return parts.filter(Boolean).join(" | ");
};

export function SearchPanel({
    searchValue,
    onSearchChange,
    onSearchSubmit,
    onSearchFocus,
    hasQuery,
    isSearchFocused,
    isSearchOpen,
    isSearching,
    searchResults,
    onSuggestionSelect,
    formRef,
    datePickerSlot,
}: SearchPanelProps) {
    const suggestionItems = searchResults.slice(0, 6);

    const renderSearchSuggestions = () => {
        if (!hasQuery || !isSearchFocused || (!isSearchOpen && !isSearching)) {
            return null;
        }
        return (
            <div className="absolute left-0 top-full z-30 mt-2 w-full rounded-2xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] p-2 shadow-[0_20px_40px_rgba(4,8,20,0.55)]">
                {isSearching ? (
                    <div className="px-3 py-2 text-xs text-[var(--hopp-muted)]">
                        Searching...
                    </div>
                ) : suggestionItems.length > 0 ? (
                    <div className="max-h-72 overflow-y-auto pr-1 scrollbar-hide">
                        {suggestionItems.map((event) => (
                            <button
                                key={event.id}
                                type="button"
                                onClick={() => onSuggestionSelect(event)}
                                className="flex w-full flex-col gap-1 rounded-xl px-3 py-2 text-left text-sm text-[var(--hopp-text)] transition hover:bg-[var(--hopp-panel-strong)]"
                            >
                                <span className="font-semibold">{event.title}</span>
                                <span className="text-xs text-[var(--hopp-muted)]">
                                    {buildSuggestionMeta(event)}
                                </span>
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="px-3 py-2 text-xs text-[var(--hopp-muted)]">
                        No matches yet.
                    </div>
                )}
            </div>
        );
    };

    return (
        <form
            ref={formRef}
            className="relative flex flex-1 items-center"
            onSubmit={onSearchSubmit}
        >
            <span className="absolute left-3 text-[var(--hopp-muted)]">
                <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                </svg>
            </span>
            <input
                type="search"
                placeholder="Search event or category"
                aria-label="Search event or category"
                value={searchValue}
                onChange={(event) => onSearchChange(event.target.value)}
                onFocus={onSearchFocus}
                className="h-10 w-full rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] pl-9 pr-11 text-sm text-[var(--hopp-text)] shadow-[0_8px_16px_rgba(3,7,18,0.35)] outline-none transition placeholder:text-[var(--hopp-muted)] focus:border-[var(--hopp-sapphire)] focus:ring-2 focus:ring-[rgba(58,92,255,0.35)]"
            />
            {datePickerSlot}
            {renderSearchSuggestions()}
        </form>
    );
}

export function MobileSearchPanel({
    searchValue,
    onSearchChange,
    onSearchSubmit,
    onSearchFocus,
    hasQuery,
    isSearchFocused,
    isSearchOpen,
    isSearching,
    searchResults,
    onSuggestionSelect,
    formRef,
    datePickerSlot,
}: SearchPanelProps) {
    const suggestionItems = searchResults.slice(0, 6);

    const renderSearchSuggestions = () => {
        if (!hasQuery || !isSearchFocused || (!isSearchOpen && !isSearching)) {
            return null;
        }
        return (
            <div className="absolute left-0 top-full z-30 mt-2 w-full rounded-2xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] p-2 shadow-[0_20px_40px_rgba(4,8,20,0.55)]">
                {isSearching ? (
                    <div className="px-3 py-2 text-xs text-[var(--hopp-muted)]">
                        Searching...
                    </div>
                ) : suggestionItems.length > 0 ? (
                    <div className="max-h-72 overflow-y-auto pr-1 scrollbar-hide">
                        {suggestionItems.map((event) => (
                            <button
                                key={event.id}
                                type="button"
                                onClick={() => onSuggestionSelect(event)}
                                className="flex w-full flex-col gap-1 rounded-xl px-3 py-2 text-left text-sm text-[var(--hopp-text)] transition hover:bg-[var(--hopp-panel-strong)]"
                            >
                                <span className="font-semibold">{event.title}</span>
                                <span className="text-xs text-[var(--hopp-muted)]">
                                    {buildSuggestionMeta(event)}
                                </span>
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="px-3 py-2 text-xs text-[var(--hopp-muted)]">
                        No matches yet.
                    </div>
                )}
            </div>
        );
    };

    return (
        <form
            ref={formRef}
            className="relative flex flex-1 items-center"
            onSubmit={onSearchSubmit}
        >
            <span className="absolute left-3 text-[var(--hopp-muted)]">
                <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                </svg>
            </span>
            <input
                type="search"
                placeholder="Search event or category"
                aria-label="Search event or category"
                value={searchValue}
                onChange={(event) => onSearchChange(event.target.value)}
                onFocus={onSearchFocus}
                className="h-10 w-full rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] pl-9 pr-11 text-base text-[var(--hopp-text)] shadow-[0_8px_16px_rgba(3,7,18,0.35)] outline-none transition placeholder:text-[var(--hopp-muted)] focus:border-[var(--hopp-sapphire)] focus:ring-2 focus:ring-[rgba(58,92,255,0.35)]"
            />
            {datePickerSlot}
            {renderSearchSuggestions()}
        </form>
    );
}
